import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AddOptionalDrill } from '@/components/training/AddOptionalDrill';
import { EmptyState } from '@/components/training/EmptyStates';
import { getLatestTrainingPlan } from '@/lib/api/training-program';
import { loadOptionalDrillCatalog } from '@/lib/training-program/optional-drill-catalog';
import { addOptionalDrill, loadOptionalDrillSelection } from '@/lib/training-program/optional-drills';
import { mapTrainingDrillToProgram } from '@/lib/training-program/program-mapper';
import type { TrainingPlan } from '@/types/curriculum';
import type { ProgramDrill } from '@/types/training-program';
import type { TrainingDrill } from '@/lib/training-program/drill-loader';

const MAX_OPTIONAL_DRILLS = 2;

export default function AddOptionalDrillPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [optionalCatalog, setOptionalCatalog] = useState<Record<string, TrainingDrill[]>>({});
  const [currentOptionalCount, setCurrentOptionalCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const response = await getLatestTrainingPlan();
      const planJson = response?.plan?.plan_json as TrainingPlan | undefined;
      if (!planJson) {
        if (!cancelled) {
          setPlan(null);
          setLoading(false);
        }
        return;
      }

      const catalog = await loadOptionalDrillCatalog();
      const optionalSelection = loadOptionalDrillSelection(planJson.id);
      if (!cancelled) {
        setPlan(planJson);
        setOptionalCatalog(catalog);
        setCurrentOptionalCount(optionalSelection.drillIds.length);
        setLoading(false);
      }
    };

    load().catch(() => {
      if (!cancelled) {
        setPlan(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const availableDrills = useMemo(() => {
    const buildCategory = (drills: TrainingDrill[]) =>
      drills.map((drill) => mapTrainingDrillToProgram(drill, { isRequired: false }));
    return {
      putting: buildCategory(optionalCatalog.putting || []),
      shortGame: buildCategory(optionalCatalog.shortGame || []),
      bunker: buildCategory(optionalCatalog.bunker || []),
    };
  }, [optionalCatalog]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!plan) {
    return (
      <EmptyState
        type="no-program"
        onAction={() => navigate('/snapshot/select')}
        actionLabel="Create Training Plan from Swing Analysis"
      />
    );
  }

  return (
    <AddOptionalDrill
      availableDrills={availableDrills as { putting: ProgramDrill[]; shortGame: ProgramDrill[]; bunker: ProgramDrill[] }}
      onBack={() => navigate(-1)}
      onAddDrill={(drill) => {
        addOptionalDrill(plan.id, drill.drill_id, MAX_OPTIONAL_DRILLS);
        navigate('/training-program');
      }}
      maxOptionalDrills={MAX_OPTIONAL_DRILLS}
      currentOptionalCount={currentOptionalCount}
    />
  );
}
